import { useEffect, useState } from "react";
import { ApiError, fetchScenarios, runScenario } from "../api";
import type { DemoRunResult, ScenarioDefinition } from "../types";
import { AutomationEvidencePanel } from "./AutomationEvidencePanel";
import { AutonomySandbox } from "./AutonomySandbox";
import { CertificatePanel } from "./CertificatePanel";
import { DataQualityPanel } from "./DataQualityPanel";
import { DecisionFlow } from "./DecisionFlow";
import { DecisionTrace } from "./DecisionTrace";
import { completeMission } from "../game";
import { MissionDock, QuickNav } from "./QuickNav";
import { ReviewQueue } from "./ReviewQueue";
import { TransferSimulator } from "./TransferSimulator";

/**
 * FDS Operations - the playground tab.
 *
 * The FDS explainer moved to the Intro tab (the landing page is now "what I'm saying",
 * this tab is "try it yourself"), so this opens straight onto the mission board and is
 * ordered as an argument rather than as a feature list:
 *
 *   1. send a transfer yourself      -> what does this system do?
 *   2. attack it                     -> when does it stop trusting itself?
 *   3. fixed scenarios incl. faults  -> what happens when it breaks?
 *   4. decision trace                -> on what basis was this decided?
 *
 * Someone who stops after step 1 still understands the product; someone who reads to step 4
 * can audit it. The aggregate numbers come from a fixed offline test set and are kept in
 * their own panel, so a single live decision is never read as evidence of a rate.
 */
export function FdsOperations() {
  const [scenarios, setScenarios] = useState<ScenarioDefinition[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [result, setResult] = useState<DemoRunResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  useEffect(() => {
    fetchScenarios()
      .then((list) => {
        setScenarios(list);
        setSelectedId(list[0]?.scenario_id ?? null);
      })
      .catch((err: unknown) => {
        setLoadError(
          err instanceof ApiError ? err.message : "시나리오 목록을 불러오지 못했습니다.",
        );
      });
  }, []);

  async function handleRun(scenarioId: string) {
    setSelectedId(scenarioId);
    setRunningId(scenarioId);
    setRunError(null);
    setResult(null);
    try {
      setResult(await runScenario(scenarioId));
      // Nudges the live-proof strip to re-count the ledger immediately.
      window.dispatchEvent(new CustomEvent("safefds:decision"));
      // A scenario really ran (fault injections included): mission 3 verified.
      completeMission("try-break");
    } catch (err) {
      setRunError(err instanceof ApiError ? err.message : "요청을 실행하지 못했습니다.");
    } finally {
      setRunningId(null);
    }
  }

  return (
    <>
      <section className="card intro">
        <p>
          여기는 <strong>라이브 시연 공간</strong>입니다. 화면 속 은행은 가상이지만, 버튼을
          누를 때마다 <strong>진짜 서버가 진짜 판정</strong>을 내립니다. 미션 여섯 개를
          순서대로 깨보세요.
        </p>
        <QuickNav />
      </section>

      <MissionDock />

      <div id="try-transfer">
        <TransferSimulator />
      </div>

      <div id="try-attack">
        <AutonomySandbox />
        <CertificatePanel />
      </div>

      <div id="try-numbers">
        <AutomationEvidencePanel />
        {/* The ledger's own self-audit sits with the other evidence: the numbers above
            come from a committed artifact, this one reads the live database (ADR-024). */}
        <DataQualityPanel />
      </div>

      <div id="try-review">
        <ReviewQueue />
      </div>

      <section className="card" id="try-break">
        <h3>🔨 일부러 고장 내보기</h3>
        <p>
          은행 시스템에서 제일 무서운 질문은 &quot;잘 될 때 얼마나 잘 되나&quot;가 아니라{" "}
          <strong>&quot;고장 났을 때 어느 쪽으로 넘어지나&quot;</strong>예요. AI가 죽었는데
          그냥 다 통과시켜 버리면? 그게 최악의 고장입니다.
        </p>
        <p className="muted">
          아래 5개 버튼 중 뒤의 두 개는 <strong>진짜로 고장을 내는</strong> 버튼이에요. 눌러서
          시스템이 위험한 쪽(전부 통과)이 아니라 안전한 쪽(사람에게)으로 넘어지는지 직접
          확인해 보세요.
        </p>

        <DecisionFlow result={result} running={runningId !== null} />

        {loadError && <p className="error-text">{loadError}</p>}
        {!scenarios && !loadError && <p className="muted">시나리오 불러오는 중…</p>}

        <div className="scenario-chips">
          {scenarios?.map((scenario) => (
            <button
              key={scenario.scenario_id}
              className={`scenario-chip ${selectedId === scenario.scenario_id ? "selected" : ""}`}
              disabled={runningId !== null}
              onClick={() => handleRun(scenario.scenario_id)}
            >
              <span className="chip-title">{scenario.title}</span>
              {scenario.simulated_failure && (
                <span className="badge simulated">일부러 고장냄</span>
              )}
              <span className="chip-desc">{scenario.description}</span>
              <span className="chip-action">
                {runningId === scenario.scenario_id ? "실행 중…" : "실행"}
              </span>
            </button>
          ))}
        </div>

        {runError && <p className="error-text">{runError}</p>}
        {result && (
          <p className="muted">
            run_id: <code>{result.run_id}</code> · HTTP {result.http_status} · status:{" "}
            {result.status}
          </p>
        )}
      </section>

      <div id="try-receipt">
        <DecisionTrace result={result} />
      </div>
    </>
  );
}
